import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Row, Col, Label, Input, Form } from "reactstrap";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Modal,
  Spinner,
} from "react-bootstrap";
import StatusBadge from "../../../../../components/StatusBatch";
import ControlledSelect from "../../../../../components/ControlledSelect";
import {
  useGetRestaurantCategoriesQuery,
  useGetRestaurantFlowTypesQuery,
  useGetRestaurantPropertiesQuery,
  useUpdateRestaurantHttpsMutation,
} from "../../../../../redux/services/restaurantServices";
import {
  categoryTagsOptionsFunctionEdit,
  formatTimestamp,
  getImageUrl,
} from "../../../../../utility/Utility";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import EditPhotoUpload from "../../../../../components/EditPhotoUpload";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faFloppyDisk,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import "./index.css";
import {
  useLazyGetRestaurantDetailsByIdQuery,
  useUpdateRestaurantImageMutation,
  useUpdateRestaurantStatusMutation,
  useUploadRestaurantLogoFileExtntionHttpsMutation,
} from "../../../../../redux/services/restaurantSearchService";
import { uploadImage } from "../../../../../components/imageUploadCompont";
// import { OwnerData } from "../../../AddRestaurant";
import ButtonComponentV2 from "../../../../../components/ButtonComponentV2";
import { useSelector } from "react-redux";
import { useLazyGetAdminsQuery } from "../../../../../redux/services/adminService";
import ControlledRowTextField from "../../../../../components/ControlledRowTextField";

type Restaurant = {
  id: string;
  documentId: string;
  restaurantId: string;
  ownerId: string;
  address: string;
  displayName: string;
  imageUrl: string;
  category: string[];
  openingTime: string;
  closingTime: string;
  type: string;
  status: string;
  restaurantProperties: string[];
  supportsDirectOrders?: boolean;
  canCollectMoney?: boolean;
  flowTypes?: any;
  acceptCartReturns: boolean;
  admins?: string[];
  deliverySettings?: {
    isFreeDelivery: boolean;
    perItemDeliveryFee: number;
  };
};

type LocationState = {
  restaurant?: Restaurant;
};

type RestaurantFormData = {
  ownerId: string;
  address: string;
  displayName: string;
  categoryTags: { label: string; value: string }[];
  admins: { label: string; value: string }[];
  imageUrl: string;
  hasOffers: boolean;
  openingTime: string;
  closingTime: string;
  type: string;
  restaurantProperties: { label: string; value: string }[];
  supportsDirectOrders?: boolean;
  canCollectMoney?: boolean;
  flowTypes: { label: string; value: string }[];
  acceptCartReturns: boolean;
  deliverySettings: {
    isFreeDelivery: boolean;
    perItemDeliveryFee: number;
  };
};

const RestaurantEdit: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { restaurant } = location.state as LocationState;
  const restaurantId =
    restaurant?.documentId || restaurant?.id || restaurant?.restaurantId;
  const restaurantDetails = useSelector(
    (state: any) => state.selectedRestaurantDetails
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(true);
  const [showImageUpdateModal, setShowImageUpdateModal] = useState(false);
  const [isImageUpdateLoading, setIsImageUpdateLoading] = useState(false);
  const [clickedButton, setClickedButton] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  // const [isSearching, setIsSearching] = useState(false);
  // const [searchOptions, setSearchOptions] = useState<OwnerData[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [trigger, { data: restaurantData, isLoading: isFetching }] =
    useLazyGetRestaurantDetailsByIdQuery();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isFreeDelivery, setIsFreeDelivery] = useState(
    restaurant?.deliverySettings?.isFreeDelivery ||
      restaurantData?.restaurant?.deliverySettings?.isFreeDelivery ||
      false
  );

  // Add admin fetching
  const [getAdmins, { data: adminsData, isLoading: isLoadingAdmins }] =
    useLazyGetAdminsQuery();

  // Photo Upload
  const [uploadedImgDetails, setUploadedImgDetails] = useState<{
    imgSrc: string;
    formData: any;
    fileExtention: string;
  }>({
    imgSrc: "",
    formData: "",
    fileExtention: "",
  });

  const imageUploadCallback = (e: any) => {
    setUploadedImgDetails({
      ...uploadedImgDetails,
      imgSrc: e.imgSrc,
      formData: e.formData,
      fileExtention: e.fileExtention,
    });
    setIsSaveButtonDisabled(false);
  };

  const handleUpdatePhoto = () => {
    setShowImageUpdateModal(true);
  };

  const handleCloseModal = () => {
    setShowImageUpdateModal(false);
    setIsSaveButtonDisabled(true);
    setUploadedImgDetails({ imgSrc: "", formData: "", fileExtention: "" });
  };

  const [getRestaurantImageReference] =
    useUploadRestaurantLogoFileExtntionHttpsMutation();

  const [updateRestaurantImage] = useUpdateRestaurantImageMutation();

  const handleRestaurantImageUpdate = async () => {
    setIsImageUpdateLoading(true);
    const imageRef = await uploadImage(getRestaurantImageReference, {
      fileExtention: uploadedImgDetails.fileExtention,
      formData: uploadedImgDetails.formData,
    });
    const updateImageResponse = await updateRestaurantImage({
      imageRef: imageRef,
      restId: restaurant?.id || restaurantData?.restaurant?.id,
      oldImgUrl: restaurant?.imageUrl || restaurantData?.restaurant?.imageUrl,
    });
    if ("data" in updateImageResponse) {
      toast.success("Cart Image Successfully Updated");
      navigate("/restaurants/restaurants-list");
    } else {
      toast.error("Something Went Wrong");
    }
    setIsImageUpdateLoading(false);
  };

  const { data: categories, isSuccess } = useGetRestaurantCategoriesQuery("");

  let categoryTagsOptions = [{ value: "", label: "" }];

  if (isSuccess) {
    categoryTagsOptions = categoryTagsOptionsFunctionEdit(categories) || [
      { value: "", label: "" },
    ];
  }

  // Admin options function
  const adminOptionsFunction = (data: any) => {
    if (data && Array.isArray(data)) {
      return data.map((admin: any) => ({
        value: admin.id || admin.userId,
        label: `${admin.displayName} <${admin.email}>` || "Unknown Admin",
      }));
    } else if (data && data.admins && Array.isArray(data.admins)) {
      return data.admins.map((admin: any) => ({
        value: admin.id || admin.userId,
        label: `${admin.displayName} <${admin.email}>` || "Unknown Admin",
      }));
    } else {
      console.error("Invalid admin data structure:", data);
      return [];
    }
  };

  // Load admins on component mount
  useEffect(() => {
    getAdmins({});
  }, [getAdmins]);

  let adminOptions = [{ value: "", label: "" }];
  if (adminsData && !isLoadingAdmins) {
    adminOptions = adminOptionsFunction(adminsData) || [
      { value: "", label: "" },
    ];
  }

  const restaurantPropertiesFunction = (data: any) => {
    if (data && data.RestaurantPropertiesList) {
      const restauarntPropertiesArray = data.RestaurantPropertiesList;
      return restauarntPropertiesArray.map((item: any) => {
        return {
          value: item.displayName,
          label: (
            <div style={{ display: "flex", justifyContent: "left", gap: "2%" }}>
              <img
                src={getImageUrl(item.displayName)}
                alt={item.displayName}
                width="20px"
                height="20px"
              />
              {item.displayName}
            </div>
          ),
        };
      });
    } else {
      console.error("Invalid data structure:", data);
      return [];
    }
  };

  const {
    data: restaurantProperties,
    isSuccess: isGetRestaurantPropertiesSuccess,
  } = useGetRestaurantPropertiesQuery("");
  let restaurantPropertiesOptions = [{ value: "", label: "" }];
  if (isGetRestaurantPropertiesSuccess) {
    restaurantPropertiesOptions = restaurantPropertiesFunction(
      restaurantProperties
    ) || [{ value: "", label: "" }];
  }

  // restaurant Flow Types
  const restaurantFlowTypesFunction = (data: any) => {
    if (data && data.FlowTypesList) {
      const restaurantFlowTypessArray = data.FlowTypesList;
      return restaurantFlowTypessArray.map((item: any) => {
        return {
          value: item.displayName,
          label: (
            <div style={{ display: "flex", justifyContent: "left", gap: "2%" }}>
              {item.displayName}
            </div>
          ),
        };
      });
    } else {
      console.error("Invalid data structure:", data);
      return [];
    }
  };

  const {
    data: restaurantFlowTypes,
    isSuccess: isGetRestaurantFlowTypesSuccess,
  } = useGetRestaurantFlowTypesQuery("");
  let restaurantFlowTypesOptions = [{ value: "", label: "" }];
  if (isGetRestaurantFlowTypesSuccess) {
    restaurantFlowTypesOptions = restaurantFlowTypesFunction(
      restaurantFlowTypes
    ) || [{ value: "", label: "" }];
  }

  const [updateRestaurant] = useUpdateRestaurantHttpsMutation();

  const initialValues = {
    ownerId: restaurant?.ownerId || "",
    address: restaurant?.address || restaurantData?.restaurant?.address || "",
    display:
      restaurantData?.restaurant?.displayName || restaurant?.displayName || "",
    imageUrl:
      restaurant?.imageUrl || restaurantData?.restaurant?.imageUrl || "",
    categoryTags:
      restaurant?.category?.map((tag) => ({
        label: tag,
        value: tag,
      })) ||
      restaurantData?.restaurant?.category?.map((tag: any) => ({
        label: tag,
        value: tag,
      })) ||
      [],
    admins:
      restaurant?.admins?.map((adminObj: any) => {
        // Handle if adminObj is just a string (adminId) or an object with id property
        const adminId = typeof adminObj === "string" ? adminObj : adminObj.id;
        const admin = adminsData?.find(
          (a: any) => (a.id || a.userId) === adminId
        );
        return {
          label: admin?.displayName || admin?.email || "Unknown Admin",
          value: adminId,
        };
      }) ||
      restaurantData?.restaurant?.admins?.map((adminObj: any) => {
        // Handle if adminObj is just a string (adminId) or an object with id property
        const adminId = typeof adminObj === "string" ? adminObj : adminObj.id;
        const admin = adminsData?.find(
          (a: any) => (a.id || a.userId) === adminId
        );
        return {
          label: admin?.displayName || admin?.email || "Unknown Admin",
          value: adminId,
        };
      }) ||
      [],
    restaurantProperties: (
      restaurant?.restaurantProperties ||
      restaurantData?.restaurantProperties ||
      []
    ).map((tag: string) => ({
      label: (
        <div style={{ display: "flex", justifyContent: "left", gap: "2%" }}>
          <img src={getImageUrl(tag)} alt={tag} width="20px" height="20px" />
          {tag}
        </div>
      ),
      value: tag,
    })),
    openingTime:
      restaurant?.openingTime || restaurantData?.restaurant?.openingTime || "",
    closingTime:
      restaurant?.closingTime || restaurantData?.restaurant?.closingTime || "",
    type: restaurant?.type || restaurantData?.restaurant?.type || "",
    supportsDirectOrders:
      restaurant?.supportsDirectOrders ||
      restaurantData?.supportsDirectOrders ||
      false,
    canCollectMoney:
      restaurant?.canCollectMoney ||
      restaurantData?.restaurant?.canCollectMoney ||
      false,
    flowTypes: (restaurant?.flowTypes || restaurantDetails?.flowType || []).map(
      (tag: string) => ({
        label: tag,
        value: tag,
      })
    ),
    acceptCartReturns:
      restaurant?.acceptCartReturns ||
      restaurantDetails?.acceptCartReturns ||
      false,
    deliverySettings: {
      isFreeDelivery:
        restaurant?.deliverySettings?.isFreeDelivery ||
        restaurantData?.restaurant?.deliverySettings?.isFreeDelivery ||
        false,
      perItemDeliveryFee:
        restaurant?.deliverySettings?.perItemDeliveryFee ||
        restaurantData?.restaurant?.deliverySettings?.perItemDeliveryFee ||
        0,
    },
  };

  const { control, handleSubmit, setValue, watch } =
    useForm<RestaurantFormData>({
      defaultValues: initialValues,
    });

  useEffect(() => {
    if (restaurantData?.restaurant) {
      setValue("ownerId", restaurantData.restaurant.ownerId);
      setValue("address", restaurantData.restaurant.address);
      setValue("displayName", restaurantData.restaurant.displayName);
      setValue("imageUrl", restaurantData.restaurant.imageUrl);
      setValue(
        "categoryTags",
        restaurantData?.restaurant?.categoryTags?.map((tag: any) => ({
          label: tag,
          value: tag,
        }))
      );

      // Update the admin setValue to handle the new structure
      setValue(
        "admins",
        restaurantData?.restaurant?.admins?.map((adminObj: any) => {
          // Handle if adminObj is just a string (adminId) or an object with id property
          const adminId = typeof adminObj === "string" ? adminObj : adminObj.id;
          const admin = adminsData?.find(
            (a: any) => (a.id || a.userId) === adminId
          );
          return {
            label: admin?.displayName || admin?.email || "Unknown Admin",
            value: adminId,
          };
        }) || []
      );

      setValue(
        "restaurantProperties",
        restaurantData?.restaurant?.restaurantProperties?.map((tag: any) => ({
          label: (
            <div style={{ display: "flex", justifyContent: "left", gap: "2%" }}>
              <img
                src={getImageUrl(tag)}
                alt={tag}
                width="20px"
                height="20px"
              />
              {tag}
            </div>
          ),
          value: tag,
        })) || []
      );
      setValue("openingTime", restaurantData.restaurant.openingTime);
      setValue("closingTime", restaurantData.restaurant.closingTime);
      setValue("type", restaurantData.restaurant.type);
      setValue(
        "supportsDirectOrders",
        restaurantData.restaurant.supportsDirectOrders
      );
      setValue("canCollectMoney", restaurantData.restaurant?.canCollectMoney);
      setValue(
        "flowTypes",
        restaurantData?.restaurant?.flowTypes?.map((tag: any) => ({
          label: tag,
          value: tag,
        })) ||
          restaurant?.flowTypes?.map((tag: any) => ({
            label: tag,
            value: tag,
          })) ||
          []
      );
      setValue(
        "deliverySettings.isFreeDelivery",
        restaurantData.restaurant.deliverySettings?.isFreeDelivery || false
      );
      setValue(
        "deliverySettings.perItemDeliveryFee",
        restaurantData.restaurant.deliverySettings?.perItemDeliveryFee || 0
      );
      setIsFreeDelivery(
        restaurantData.restaurant.deliverySettings?.isFreeDelivery || false
      );

      setValue(
        "acceptCartReturns",
        restaurant?.acceptCartReturns ||
          restaurantDetails?.acceptCartReturns ||
          false
      );
    }
    if (restaurantId) {
      trigger(restaurantId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    restaurantData,
    setValue,
    restaurantId,
    trigger,
    restaurant?.acceptCartReturns,
    restaurantDetails?.acceptCartReturns,
    adminsData,
  ]);

  const [updateRestaurantStatus, { isLoading: isStatusUpdateLoading }] =
    useUpdateRestaurantStatusMutation();

  const handleUpdateRestaurantStatus = async (status: string) => {
    if (!restaurant) return;

    setClickedButton(status);

    try {
      const response = await updateRestaurantStatus({
        restaurantId: restaurant?.id || restaurantData?.restaurant?.id,
        status,
      }).unwrap();
      if (response === "OK") {
        toast.success(`Restaurant status updated to ${status}`);
        navigate("/restaurants/restaurants-list");
        setClickedButton(null);
      }
    } catch (error) {
      toast.error("Failed to update restaurant status. Please try again.");
      setClickedButton(null);
    }
  };

  const onSubmit = async (data: RestaurantFormData) => {
    setIsLoading(true);
    const categoryTags = data?.categoryTags?.map((item) => item.value);
    const admins = data?.admins?.map((item) => item.value);
    const restaurantProperties = data?.restaurantProperties?.map(
      (item) => item.value
    );
    const flowTypes = data?.flowTypes?.map((item: any) => item.value);

    const updatedData = {
      ...data,
      categoryTags: categoryTags,
      admins: admins,
      restaurantProperties: restaurantProperties,
      supportsDirectOrders: data.supportsDirectOrders,
      canCollectMoney: data.canCollectMoney,
      flowTypes: flowTypes,
      deliverySettings: {
        isFreeDelivery: data.deliverySettings.isFreeDelivery,
        perItemDeliveryFee:
          parseFloat(
            data.deliverySettings.perItemDeliveryFee.toLocaleString()
          ) || 0,
      },
    };
    try {
      const response = await updateRestaurant({
        restaurantId: restaurant?.id || restaurantData?.restaurant?.id,
        data: updatedData,
      });
      if ("data" in response) {
        navigate("/restaurants/restaurants-list");
        toast.success("Restaurant Updated");
      } else {
        setIsLoading(false);
        toast.error("Failed to update restaurant");
      }
    } catch (error) {
      setIsLoading(false);
      toast.error("Failed to update restaurant");
      console.error("Failed to update restaurant:", error);
    }
  };

  const handleEdit = () => {
    setIsEditMode(!isEditMode);
  };

  // const [triggerRestaurantSearchList] = useGetOwnerSearchResultsMutation();

  // const handleSearch = async (inputValue: any) => {
  //   setIsSearching(true);
  //   if (inputValue.length > 1) {
  //     try {
  //       const obj = { params: `query=${inputValue}` };
  //       const response: any = await triggerRestaurantSearchList(obj);

  //       if (response?.data) {
  //         const options = response.data.hits.map((item: any) => ({
  //           value: item.documentId,
  //           label: item.displayName,
  //         }));
  //         setSearchOptions(options);
  //       } else if (response?.errors) {
  //         toast.error("Something went wrong...! Please try again.");
  //       }
  //     } catch (error) {
  //       toast.error("Something went wrong...! Please try again.");
  //     }
  //   } else {
  //     setSearchOptions([]);
  //   }
  //   setIsSearching(false);
  // };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!(event.target as HTMLElement).closest(".autocomplete")) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (!restaurant && !restaurantData?.restaurant) {
    return <div>No restaurant details available.</div>;
  }

  return (
    <>
      <Card className="mt-4">
        {isFetching ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "200px" }}
          >
            <Spinner animation="border" variant="primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <span className="ms-2">Please Wait</span>
          </div>
        ) : (
          <>
            <CardHeader className="d-flex justify-content-between bg-white">
              <h4>
                Edit{" "}
                {restaurant?.displayName ||
                  restaurantData?.restaurant?.displayName}{" "}
                Details
              </h4>
              <Button variant="light" onClick={handleEdit}>
                <FontAwesomeIcon icon={faEdit} title="Edit" color="#699BF7" />
              </Button>
            </CardHeader>
            <CardBody className="restaurant-search-offcanvas-content">
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Row className="mb-3">
                  <Col lg="4">
                    <Label>Restaurant Photo</Label>
                  </Col>
                  <Col lg="8" md="10" sm="12" xs="12">
                    <div
                      style={{
                        position: "relative",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: "10px",
                      }}
                    >
                      <img
                        src={
                          restaurant?.imageUrl ||
                          restaurantData?.restaurant?.imageUrl
                        }
                        alt="cartimage"
                        style={{
                          width: "150px",
                          height: "150px",
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      />
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={handleUpdatePhoto}
                        style={{
                          position: "absolute",
                          bottom: "10px",
                          left: "58%",
                          transform: "translateX(-50%)",
                          borderRadius: "50%",
                        }}
                        title="Edit Photo"
                        disabled={!isEditMode}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Button>
                    </div>
                  </Col>
                </Row>
                <hr />
                <Row className="mb-3">
                  <Col lg="4">
                    <Label>Name</Label>
                  </Col>
                  <Col lg="8">
                    <Controller
                      name="displayName"
                      control={control}
                      render={({ field }) => (
                        <Input {...field} disabled={!isEditMode} />
                      )}
                    />
                  </Col>
                </Row>
                <hr />

                {/* Add Assigned Admins Section */}
                <ControlledSelect
                  label={"Assigned Admins"}
                  inputName={"admins"}
                  control={control}
                  isMulti={true}
                  optionsList={adminOptions}
                  isDisabled={!isEditMode}
                  isLoading={isLoadingAdmins}
                />
                <hr />

                <Row className="mb-3">
                  <Col lg="4">
                    <Label>Live Location</Label>
                  </Col>
                  <Col lg="8">
                    <Controller
                      name="address"
                      control={control}
                      render={({ field }) => (
                        <Input {...field} disabled={true} />
                      )}
                      disabled={!isEditMode}
                    />
                  </Col>
                </Row>
                <hr />
                <ControlledSelect
                  label={"Categories"}
                  inputName={"categoryTags"}
                  control={control}
                  isMulti={true}
                  optionsList={categoryTagsOptions}
                  isDisabled={!isEditMode}
                />
                <hr />
                <ControlledSelect
                  label={"Restaurant Properties"}
                  inputName={"restaurantProperties"}
                  control={control}
                  isMulti={true}
                  optionsList={restaurantPropertiesOptions}
                  isDisabled={!isEditMode}
                />
                <hr />
                <ControlledSelect
                  label={"Flow Types"}
                  inputName={"flowTypes"}
                  control={control}
                  isMulti={true}
                  optionsList={restaurantFlowTypesOptions}
                  isDisabled={!isEditMode}
                />
                <hr />
                <Row className="mb-3">
                  <Col lg="4">
                    <Label>Opening Time</Label>
                  </Col>
                  <Col lg="8">
                    <Controller
                      name="openingTime"
                      control={control}
                      render={({ field }) => <Input type="time" {...field} />}
                      disabled={!isEditMode}
                    />
                  </Col>
                </Row>
                <hr />
                <Row className="mb-3">
                  <Col lg="4">
                    <Label>Closing Time</Label>
                  </Col>
                  <Col lg="8">
                    <Controller
                      name="closingTime"
                      control={control}
                      render={({ field }) => <Input type="time" {...field} />}
                      disabled={!isEditMode}
                    />
                  </Col>
                </Row>
                <hr />
                <Row className="mb-3">
                  <Col lg="4">
                    <Label>Status</Label>
                  </Col>
                  <Col lg="8">
                    <StatusBadge
                      status={
                        restaurant?.status || restaurantData?.restaurant?.status
                      }
                    />
                  </Col>
                </Row>

                <hr />
                <Row>
                  <Col lg="4">
                    <Label
                      className="form-check-label"
                      htmlFor="supportsDirectOrders"
                    >
                      Can Direct Pickup
                    </Label>
                  </Col>
                  <Col lg="8">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="supportsDirectOrders"
                      checked={watch("supportsDirectOrders")}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setValue("supportsDirectOrders", event.target.checked);
                      }}
                      disabled={!isEditMode}
                    />
                  </Col>
                </Row>
                <hr />
                <Row>
                  <Col lg="4">
                    <Label
                      className="form-check-label"
                      htmlFor="canCollectMoney"
                    >
                      Can Collect Money
                    </Label>
                  </Col>
                  <Col lg="8">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="canCollectMoney"
                      checked={watch("canCollectMoney")}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setValue("canCollectMoney", event.target.checked);
                      }}
                      disabled={!isEditMode}
                    />
                  </Col>
                </Row>
                <hr />
                <Row>
                  <Col lg="4">
                    <Label
                      className="form-check-label"
                      htmlFor="acceptCartReturns"
                    >
                      Accept Cart Return Requests
                    </Label>
                  </Col>
                  <Col lg="8">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="acceptCartReturns"
                      checked={watch("acceptCartReturns")}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setValue("acceptCartReturns", event.target.checked);
                      }}
                      disabled={!isEditMode}
                    />
                  </Col>
                </Row>
                <hr />
                <Row className="mb-3">
                  <Col lg="4">
                    <Label>Created At</Label>
                  </Col>
                  <Col lg="8">
                    {formatTimestamp(restaurantData?.restaurant?.createdAt)}
                  </Col>
                </Row>
                <hr />
                <Row>
                  <Col lg="4">
                    <Label
                      className="form-check-label"
                      htmlFor="isFreeDelivery"
                    >
                      Free Delivery
                    </Label>
                  </Col>
                  <Col lg="8">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="isFreeDelivery"
                      checked={watch("deliverySettings.isFreeDelivery")}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setValue(
                          "deliverySettings.isFreeDelivery",
                          event.target.checked
                        );
                        setIsFreeDelivery(event.target.checked);
                      }}
                      disabled={!isEditMode}
                    />
                  </Col>
                </Row>
                <hr />
                
                  <ControlledRowTextField
                  label={"Per Item Delivery Fee"}
                  inputName={"deliverySettings.perItemDeliveryFee"}
                  inputType={"number"}
                  placeholder={"Enter delivery fee"}
                  control={control}
                  isRequired={true}
                  rules={{
                    required: "Delivery fee is required",
                    min: {
                      value: 0,
                      message: "Delivery fee cannot be negative",
                    },
                  }}
                  isDisabled={
                    !isEditMode
                  }
                />

              
                <br/>
                <div className="d-flex justify-content-end align-items-center ">
                  {restaurant?.status === "Pending" && (
                    <>
                      <Button
                        className="edit-restaurent-btn-style"
                        variant="success"
                        onClick={() => handleUpdateRestaurantStatus("Approved")}
                        disabled={isStatusUpdateLoading}
                      >
                        {clickedButton === "Approved" &&
                        isStatusUpdateLoading ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin />
                            &nbsp; Approving
                          </>
                        ) : (
                          <>Approve</>
                        )}
                      </Button>
                      &nbsp;
                      <Button
                        className="edit-restaurent-btn-style"
                        variant="danger"
                        onClick={() => handleUpdateRestaurantStatus("Rejected")}
                        disabled={isStatusUpdateLoading}
                      >
                        {clickedButton === "Rejected" &&
                        isStatusUpdateLoading ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin />
                            &nbsp; Rejecting
                          </>
                        ) : (
                          <>Reject</>
                        )}
                      </Button>
                    </>
                  )}
                  {restaurant?.status === "Approved" && (
                    <ButtonComponentV2
                      icon={
                        clickedButton === "Rejected" &&
                        isStatusUpdateLoading ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin />
                            &nbsp; Rejecting
                          </>
                        ) : (
                          <>Reject</>
                        )
                      }
                      color="#fff"
                      backgroundColor="#DC3545"
                      size="medium"
                      isDisabled={isStatusUpdateLoading}
                      isHidden={false}
                      onClick={() => handleUpdateRestaurantStatus("Rejected")}
                    />
                  )}

                  {restaurant?.status === "Rejected" && (
                    <ButtonComponentV2
                      icon={
                        clickedButton === "Approved" &&
                        isStatusUpdateLoading ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin />
                            &nbsp; Approving
                          </>
                        ) : (
                          <>Approve</>
                        )
                      }
                      color="#fff"
                      backgroundColor="#198754"
                      size="medium"
                      isDisabled={isStatusUpdateLoading}
                      isHidden={false}
                      onClick={() => handleUpdateRestaurantStatus("Approved")}
                    />
                  )}
                  {isLoading ? (
                    <ButtonComponentV2
                      titel="Saving..."
                      icon={<FontAwesomeIcon icon={faSpinner} spin />}
                      color="#fff"
                      backgroundColor="#590B21"
                      size="medium"
                      isDisabled={true}
                      isHidden={false}
                    />
                  ) : (
                    <ButtonComponentV2
                      titel="Save"
                      type="submit"
                      icon={<FontAwesomeIcon icon={faFloppyDisk} />}
                      color="#fff"
                      backgroundColor="#590B21"
                      size="medium"
                      isDisabled={!isEditMode}
                      isHidden={false}
                    />
                  )}
                </div>
              </Form>
              <Modal
                show={showImageUpdateModal}
                onHide={handleCloseModal}
                centered
                backdrop="static"
              >
                <Modal.Header closeButton>
                  <Modal.Title>Update Profile Photo</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <EditPhotoUpload
                    imageUrl={uploadedImgDetails.imgSrc}
                    onUpload={imageUploadCallback}
                  />
                </Modal.Body>
                <Modal.Footer>
                  {isImageUpdateLoading ? (
                    <>
                      <ButtonComponentV2
                        titel="Saving..."
                        icon={<FontAwesomeIcon icon={faSpinner} spin />}
                        isDisabled={
                          isSaveButtonDisabled || isImageUpdateLoading
                        }
                        color="#fff"
                        backgroundColor="#590B21"
                      />
                    </>
                  ) : (
                    <ButtonComponentV2
                      titel="Save"
                      isDisabled={isSaveButtonDisabled || isImageUpdateLoading}
                      onClick={handleRestaurantImageUpdate}
                      color="#fff"
                      backgroundColor="#590B21"
                    />
                  )}
                </Modal.Footer>
              </Modal>
            </CardBody>
          </>
        )}
      </Card>
    </>
  );
};

export default RestaurantEdit;
